import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import { useToast } from '@/contexts/admin/ToastContext';
import { maintenanceApi, usePostMaintenanceTicketMutation } from '@/store/api/maintenanceApi';
import { buildTicketFormData } from '@/utils/residents/maintenance/buildTicketFormData';
import {
  buildSubmitPayload,
  MaintenanceSubmitArgs,
} from '@/utils/residents/maintenance/buildSubmitPayload';
import { buildOptimisticTicket } from '@/utils/residents/maintenance/buildOptimisticTicket';
import { extractErrorMessage } from '@/utils/common/errorHandling';

export type { MaintenanceSubmitArgs };

export interface MaintenanceSubmitResult {
  success: boolean;
  error?: any;
}

let optimisticCounter = -1;

export const useMaintenanceSubmit = () => {
  const [post, status] = usePostMaintenanceTicketMutation();
  const { showError } = useToast();
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user as any);

  const submitTicket = useCallback(
    async (args: MaintenanceSubmitArgs): Promise<MaintenanceSubmitResult> => {
      // Optimistically insert a stub so the new request shows in the list instantly.
      const stub = buildOptimisticTicket(args, new Date().toISOString(), optimisticCounter--);
      const patch = dispatch(
        maintenanceApi.util.updateQueryData('getMaintenanceList', undefined, (draft) => {
          draft.response.unshift(stub);
        }),
      );

      try {
        const payload = buildSubmitPayload(args, user?.phoneNumber);
        const formData = buildTicketFormData(payload);
        await post(formData).unwrap();
        // Success: the mutation's invalidatesTags refetch replaces the stub with real data.
        return { success: true };
      } catch (e) {
        patch.undo();
        console.error('Failed to submit maintenance request:', e);
        showError(extractErrorMessage(e, 'Failed to submit work request. Please try again.'));
        return { success: false, error: e };
      }
    },
    [post, dispatch, user, showError],
  );

  return { submitTicket, status };
};
